import { useState, useEffect } from "react";

import { useIntl } from "react-intl";

import { createGisLayer, getAllGisLayers } from "services/requests/v1/gislayer";

import {
    GisLayerTableType,
    SetRequestError,
    SubscribeContentType,
    UnsubscribeContentType,
} from "../../types/generic/index";

import GoAiguaButton from "components/generic/goaiguabutton";

type CreateNewLayerType = {
    contentName: string;
    setIsOpen: (isOpen: boolean) => void;
    setRowData: (rowData: GisLayerTableType[]) => void;
    subscribeContent: SubscribeContentType;
    unsubscribeContent: UnsubscribeContentType;
    setError: SetRequestError;
};

const CreateNewLayer = ({
    contentName,
    setIsOpen,
    setRowData,
    setError,
    subscribeContent,
    unsubscribeContent,
}: CreateNewLayerType) => {
    const intl = useIntl();

    const [newLayer, setNewLayer] = useState<string>();
    const [errorLayer, setErrorLayer] = useState<boolean>(false);
    const [checkStatus, setCheckStatus] = useState<"enabled" | "disabled">(
        "disabled"
    );

    useEffect(() => {
        if (newLayer) {
            setCheckStatus("enabled");
        } else {
            setCheckStatus("disabled");
        }
    }, [newLayer]);

    const handleSaveClick = async () => {
        if (!newLayer) return;

        subscribeContent(contentName);

        try {
            if (newLayer) {
                const JSONNewLayer = JSON.parse(newLayer);
                if (Array.isArray(JSONNewLayer)) {
                    const layerPromises: Array<Promise<any>> = [];
                    JSONNewLayer.map(async (newLayerUnit) => {
                        if (newLayerUnit.alias && newLayerUnit.layerType) {
                            if (!newLayerUnit["layerId"]) {
                                newLayerUnit.layerId = newLayerUnit["id"];
                            }
                            delete newLayerUnit["id"];
                            layerPromises.push(createGisLayer(newLayerUnit));
                        } else {
                            setErrorLayer(true);
                        }
                    });

                    Promise.all(layerPromises).then(() => {
                        setIsOpen(false);

                        getAllGisLayers().then((res) => {
                            setRowData(res.data);
                        });
                    });
                } else {
                    if (JSONNewLayer.alias && JSONNewLayer.layerType) {
                        if (!JSONNewLayer["layerId"]) {
                            JSONNewLayer.layerId = JSONNewLayer["id"];
                        }
                        delete JSONNewLayer["id"];
                        await createGisLayer(JSONNewLayer);
                        setIsOpen(false);

                        getAllGisLayers().then((res) => {
                            setRowData(res.data);
                        });
                    } else {
                        setErrorLayer(true);
                    }
                }
            } else {
                setErrorLayer(true);
            }

            unsubscribeContent(contentName, true);
        } catch (error) {
            setErrorLayer(true);
            setError(contentName);
        }
    };

    return (
        <>
            <div className="c-create-new-layer__container">
                <div className="c-create-new-layer__container__title">
                    {intl.formatMessage({
                        id: "app.gis.write_or_paste_JSON",
                    })}
                </div>
                <textarea
                    className="c-create-new-layer__container__textarea"
                    value={newLayer}
                    onChange={(ev) => {
                        setNewLayer(ev.target.value);
                    }}
                />
                <div className="c-create-new-layer__container__paste-error">
                    <GoAiguaButton
                        text={intl.formatMessage({
                            id: "app.gis.paste_JSON",
                        })}
                        className="c-create-new-layer__paste-button"
                        onClick={() =>
                            navigator.clipboard.readText().then((res) => {
                                setNewLayer(res);
                            })
                        }
                    />
                    {errorLayer && (
                        <div className="c-create-new-layer__container__error">
                            {intl.formatMessage({ id: "app.gis.error_JSON" })}
                        </div>
                    )}
                </div>
            </div>
            <div className="c-create-new-layer__container__cancel-save-buttons">
                <GoAiguaButton
                    text={intl.formatMessage({ id: "app.gis.cancel" })}
                    className="c-create-new-layer__cancel-button"
                    onClick={() => {
                        setIsOpen(false);
                    }}
                />
                <GoAiguaButton
                    text={intl.formatMessage({ id: "app.users.save" })}
                    status={checkStatus}
                    className="c-create-new-layer__save-button"
                    onClick={handleSaveClick}
                />
            </div>
        </>
    );
};

export default CreateNewLayer;
